import type {
  InstrumentId,
  NotationTimeline,
  PracticeLevel,
  RecordedSample,
  SessionReport,
} from '@swarasense/shared';
import type { SessionRecording } from './results.js';

/**
 * The single page's session state (originally Task 3's cross-screen store, for
 * the pre-Phase-4b setup/live/summary router; that router is gone, but the
 * shape — controls, the in-flight take, the finished report — is unchanged):
 * one object instead of scattered globals, read and written by `app.ts` and
 * the top bar's Settings/Sa popovers, with `app.ts`'s own `store.subscribe`
 * reacting to whatever any of them change (rescoring on ragam change,
 * restarting a running drone on Sa change, etc.).
 */
export interface SessionState {
  pitchClass: string;
  octave: number;
  ragaName: string;
  thaalamName: string;
  tempo: number;
  instrument: InstrumentId;
  level: PracticeLevel;
  droneMode: 'inapp' | 'external';
  droneVolume: number;
  thaalamVolume: number;
  micGain: number;
  /** Task 2 (targeted drills): `'drill'` means the stage is showing the
   * `NotationLane` practice view for `timeline` below (metronome driven by
   * the timeline's own thaalam/tempo, cursor follow-along, take handed off
   * to Task 3's gentle per-note review on Stop) instead of free practice.
   * `'composition'` remains reserved for a later mode. Ear-training plan
   * Task 3: `'ear'` means the stage's ribbon/live-row are hidden and
   * `#ear-training-mount` (`app.ts`) is showing `ear-training.ts`'s
   * swara-ID round instead — no mic/recording involved, so unlike `'drill'`
   * this mode never touches `timeline`/`drillTargetSwara`/`drillLabel`
   * below. `ear-training.ts` owns the round's own internal state (current
   * question, results) itself; this flag only tells `app.ts` which section
   * of the stage to show. */
  practiceMode: 'free' | 'composition' | 'drill' | 'ear';
  /** The drill/exercise/song currently loaded for practice mode — set when
   * entering it (Task 4 wires the real "try this drill" launch; Task 2 adds
   * a dev-only trigger so the flow is exercisable before that lands) and
   * cleared on exit. `undefined` whenever `practiceMode !== 'drill'`. */
  timeline?: NotationTimeline | undefined;
  /** Task 5 polish: the drill's ACTUAL target swara (the raw 12-symbol
   * `generateDrill` was called with), carried alongside `timeline` so
   * anything needing "what is this drill actually about" (the practice
   * banner's fallback label, the gentle review's headline) reads the real
   * target instead of re-inferring it from `timeline.notes.at(-1)` — a
   * guess that happens to hold for today's anchor+approach templates (they
   * all end on the target) but isn't a contract `NotationTimeline` makes.
   * `undefined` whenever `practiceMode !== 'drill'`. */
  drillTargetSwara?: string | undefined;
  /** Final-fix (targeted drills): the exact warm banner label `enterPracticeMode`
   * resolved and displayed at entry (either the caller's own `label`, e.g.
   * "Warm-up: {raga} — {SwaraName}", or its "Practice: {raga} — {swara}"
   * fallback) — carried in the store so "Try again" (Stop's review) can
   * re-enter practice mode with the SAME label instead of falling back to
   * the raw 12-symbol `targetSwara` key (e.g. "— d"), which is what
   * happened when `onTryAgain` called `enterPracticeMode` with no label at
   * all. `undefined` whenever `practiceMode !== 'drill'`. */
  drillLabel?: string | undefined;
  /** Typed `| undefined` (not just optional) so changing the shruti pitch
   * class/octave on Setup can explicitly clear a now-stale lock rather than
   * leaving a calibration for the previous shruti in effect (see scoring.ts's
   * `saHzFor`, which is what actually consumes this for grading). */
  calibration?: { locked: boolean; saHz: number } | undefined;
  /** Song lookup result (FR-2/FR-13), carried from setup's "Find" through to
   * summary's coaching request. Not in the Task 3 brief's interface sketch,
   * but required to preserve the existing "coaching knows the song" behavior
   * now that lookup (setup) and coaching (summary) are different screens. */
  songName?: string;
  songNotation?: string;
  /** Typed `| undefined` (not just optional) so Task 3's "New take" affordance
   * can explicitly clear a finished take's report rather than leaving a stale
   * one in the store once the results panel re-arms for the next take. */
  report?: SessionReport | undefined;
  /** Typed `| undefined` (not just optional) so a take that produced no
   * recording can explicitly clear a stale one left over from an earlier take. */
  recording?: SessionRecording | undefined;
  recSamples: RecordedSample[];
}

export interface Store {
  get(): SessionState;
  set(patch: Partial<SessionState>): void;
  /** Registers `fn` to be called with the new state after every `set`. Returns
   * a function that unregisters it. */
  subscribe(fn: (state: SessionState) => void): () => void;
}

/**
 * A minimal pub/sub store. `set` shallow-merges `patch` into the current state
 * and synchronously notifies every subscriber with the merged result;
 * `subscribe` returns an unsubscribe function.
 */
export function createStore(initial: SessionState): Store {
  let state = initial;
  const subscribers = new Set<(state: SessionState) => void>();

  return {
    get(): SessionState {
      return state;
    },
    set(patch: Partial<SessionState>): void {
      state = { ...state, ...patch };
      for (const fn of subscribers) fn(state);
    },
    subscribe(fn: (state: SessionState) => void): () => void {
      subscribers.add(fn);
      return () => subscribers.delete(fn);
    },
  };
}
